const READINESS_FIELD_BY_STEP = Object.freeze({
  companyCurrency: "company_currency",
  treasuryAccount: "treasury_account",
  paymentModes: "payment_modes",
  paymentRouting: "payment_routing",
});

const formatCount = (count, singular, plural = `${singular}s`) => {
  const normalizedCount = Math.max(0, Number(count) || 0);
  return `${normalizedCount} ${normalizedCount > 1 ? plural : singular}`;
};

const buildCurrencyDetail = (readiness = {}) => {
  const currency = readiness.company_currency || {};
  if (currency.ready) {
    const label = [
      currency.reference_currency_name,
      currency.reference_currency_code
        ? `(${currency.reference_currency_code})`
        : null,
    ]
      .filter(Boolean)
      .join(" ");
    return `Devise de référence configurée${label ? ` : ${label}` : ""}.`;
  }

  const availableCount = Number(currency.available_currency_count) || 0;
  if (availableCount > 0) {
    return `${formatCount(
      availableCount,
      "devise"
    )} existe${availableCount > 1 ? "nt" : ""}, mais aucune n'est définie comme devise de référence de l'entreprise.`;
  }

  return "Aucune devise de référence n'est configurée pour l'entreprise.";
};

const buildTreasuryAccountDetail = (readiness = {}) => {
  const currency = readiness.company_currency || {};
  const account = readiness.treasury_account || {};
  if (!currency.ready) {
    return "Configurez d'abord la devise de référence de l'entreprise.";
  }
  if (account.ready) {
    return `${formatCount(
      account.compatible_count,
      "compte commercial actif",
      "comptes commerciaux actifs"
    )} compatible${Number(account.compatible_count) > 1 ? "s" : ""} avec la devise de référence pour cet établissement.`;
  }
  if (Number(account.active_count) > 0) {
    return `${formatCount(
      account.active_count,
      "compte commercial actif",
      "comptes commerciaux actifs"
    )}, mais aucun n'est compatible avec la devise de référence.`;
  }
  return "Aucun compte de trésorerie commercial actif n'est disponible pour cet établissement.";
};

const buildPaymentModesDetail = (readiness = {}) => {
  const modes = readiness.payment_modes || {};
  if (modes.ready) {
    return `${formatCount(
      modes.count,
      "mode de paiement disponible",
      "modes de paiement disponibles"
    )} pour le POS.`;
  }
  return "Aucun mode de paiement n'est disponible pour le POS.";
};

const buildPaymentRoutingDetail = (readiness = {}) => {
  const routing = readiness.payment_routing || {};
  const modes = readiness.payment_modes || {};
  if (!modes.ready) {
    return "Créez d'abord au moins un mode de paiement.";
  }
  if (routing.ready) {
    return `${routing.covered_count}/${routing.total_count} mode(s) de paiement disposent d'un compte utilisable par le POS.`;
  }

  const missingModes = Array.isArray(routing.missing_modes)
    ? routing.missing_modes
    : [];
  const visibleNames = missingModes
    .slice(0, 3)
    .map((mode) => mode.payment_mode_name)
    .filter(Boolean);
  const remainingCount = Math.max(0, missingModes.length - visibleNames.length);
  const names = visibleNames.length
    ? ` : ${visibleNames.join(", ")}${
        remainingCount > 0 ? ` et ${remainingCount} autre(s)` : ""
      }`
    : "";
  return `Routage ou compte par défaut manquant${names}.`;
};

const buildReadinessDetail = (doneWhen, readiness, fallbackDetail) => {
  switch (doneWhen) {
    case "companyCurrency":
      return buildCurrencyDetail(readiness);
    case "treasuryAccount":
      return buildTreasuryAccountDetail(readiness);
    case "paymentModes":
      return buildPaymentModesDetail(readiness);
    case "paymentRouting":
      return buildPaymentRoutingDetail(readiness);
    default:
      return fallbackDetail;
  }
};

export const resolveContextStepPresentation = ({
  workspaceReady,
  readinessStatus,
  readiness,
  companyId,
  warehouseId,
}) => {
  if (!workspaceReady) {
    return {
      done: false,
      detail: "Sélectionnez une entreprise et un établissement avant les opérations métier.",
      tag: { color: "default", label: "Contexte requis" },
    };
  }

  if (readinessStatus === "idle" || readinessStatus === "loading") {
    return {
      done: false,
      detail: `Vérification de l'entreprise ${companyId || "-"} et de l'établissement ${
        warehouseId || "-"
      }…`,
      tag: { color: "processing", label: "Vérification" },
    };
  }

  if (readinessStatus === "error") {
    return {
      done: false,
      detail:
        "Le contexte est sélectionné, mais son état actif n'a pas pu être confirmé.",
      tag: { color: "warning", label: "Non vérifié" },
    };
  }

  const context = readiness?.context || {};
  const companyLabel = context.company_name || `Entreprise #${companyId || "-"}`;
  const warehouseLabel =
    context.warehouse_name || `Établissement #${warehouseId || "-"}`;
  if (context.ready) {
    return {
      done: true,
      detail: `${companyLabel} et ${warehouseLabel} sont actifs.`,
      tag: { color: "success", label: "Contexte actif" },
    };
  }

  const inactiveResources = [];
  if (
    !["active", "enabled"].includes(
      String(context.company_status || "").toLowerCase()
    )
  ) {
    inactiveResources.push("l'entreprise");
  }
  if (
    !["active", "enabled"].includes(
      String(context.warehouse_status || "").toLowerCase()
    )
  ) {
    inactiveResources.push("l'établissement");
  }
  return {
    done: false,
    detail: `${
      inactiveResources.join(" et ") || "Le contexte"
    } n'est pas actif.`,
    tag: { color: "error", label: "Contexte inactif" },
  };
};

export const resolveOnboardingStepPresentation = ({
  step,
  readinessStatus,
  readiness,
  workspaceReady,
}) => {
  if (!step?.blocking || !step?.doneWhen) {
    return {
      done: Boolean(step?.done),
      detail: step?.detail || "",
      tag: null,
      actionLabel: "Ouvrir",
    };
  }

  if (!workspaceReady) {
    return {
      done: false,
      detail:
        "Sélectionnez d'abord une entreprise et un établissement pour vérifier cette condition.",
      tag: { color: "default", label: "Contexte requis" },
      actionLabel: "Configurer",
    };
  }

  if (readinessStatus === "idle" || readinessStatus === "loading") {
    return {
      done: false,
      detail: "Vérification de la configuration réelle en cours…",
      tag: { color: "processing", label: "Vérification" },
      actionLabel: "Ouvrir",
    };
  }

  if (readinessStatus === "error") {
    return {
      done: false,
      detail:
        "Le contrôle automatique est indisponible. L'état de cette condition n'est pas confirmé.",
      tag: { color: "warning", label: "Non vérifié" },
      actionLabel: "Ouvrir",
    };
  }

  const readinessField = READINESS_FIELD_BY_STEP[step.doneWhen];
  const done = Boolean(readinessField && readiness?.[readinessField]?.ready);
  return {
    done,
    detail: buildReadinessDetail(step.doneWhen, readiness, step.detail),
    tag: done
      ? { color: "success", label: "Validé" }
      : { color: "error", label: "Bloque le POS" },
    actionLabel: done ? "Voir" : "Configurer",
  };
};

export { READINESS_FIELD_BY_STEP };
